import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, CheckCircle, Loader2, ShieldCheck, Users, Sparkles } from "lucide-react";
import { Reveal, PageHero } from "@/components/bits";
import { API_URL, ACTIVITIES } from "@/data/site";

const inputCls =
  "w-full border border-white/12 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-zinc-500 transition-colors focus:border-red-500 focus:outline-none";
const labelCls = "mb-2 block font-mono2 text-[10px] uppercase tracking-[0.22em] text-zinc-400";

const FileField = ({ label, name, file, onChange, testid }) => (
  <div>
    <label className={labelCls} htmlFor={testid}>{label}</label>
    <label
      htmlFor={testid}
      className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-white/20 bg-white/[0.02] px-4 py-6 text-sm text-zinc-400 transition-colors hover:border-red-500/60 hover:text-red-300"
    >
      <Upload size={16} aria-hidden="true" />
      {file ? file.name : "Click to upload (PDF, DOC, JPG, PNG — max 10 MB)"}
    </label>
    <input id={testid} name={name} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" className="hidden" onChange={onChange} data-testid={testid} />
  </div>
);

const Careers = () => {
  const empty = { name: "", phone: "", email: "", expertise: "", experience: "", role: "", message: "", company: "" };
  const [form, setForm] = useState(empty);
  const [resume, setResume] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (resume) fd.append("resume", resume);
      if (portfolio) fd.append("portfolio", portfolio);
      await axios.post(`${API_URL}/careers`, fd);
      setDone(true);
      toast.success("Application submitted successfully.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Careers"
        title={<>Build your career <span className="text-red-500">with us</span></>}
        sub="We welcome applications from passionate instructors, performers and creative professionals across our activity categories. Share your details — our team reviews every application."
      />

      <section className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-8" data-testid="careers-page">
        <div className="grid gap-10 lg:grid-cols-3">
          <Reveal className="lg:col-span-1">
            <div className="space-y-5">
              {[
                { icon: ShieldCheck, title: "A Disciplined Workplace", desc: "Punctuality, consistency and professionalism — the same values we teach." },
                { icon: Users, title: "Multi-Activity Platform", desc: "Dance, fitness, music, martial arts and performance — teach what you love." },
                { icon: Sparkles, title: "30+ Years of Legacy", desc: "Grow with an established academy trusted by generations of students." },
              ].map((v) => (
                <div key={v.title} className="card-glow border border-white/10 bg-[#0F0F14] p-7">
                  <v.icon size={22} className="text-red-500" aria-hidden="true" />
                  <h3 className="mt-4 font-display text-sm font-extrabold uppercase tracking-wide text-white">{v.title}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-zinc-400">{v.desc}</p>
                </div>
              ))}
              <p className="text-xs leading-relaxed text-zinc-500">
                Submitting an application does not guarantee employment. Shortlisted candidates are contacted by
                the academy directly.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="border border-white/10 bg-[#0F0F14] p-6 sm:p-10">
              {done ? (
                <div className="py-10 text-center" data-testid="career-success">
                  <CheckCircle size={44} className="mx-auto text-red-500" />
                  <h3 className="mt-6 font-display text-2xl font-black uppercase tracking-tight">Thank You!</h3>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
                    Your application has been successfully submitted to New Boogie Woogie Academy. Our team will
                    review it and contact you using the details provided.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="relative grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="career-form">
                  <div className="absolute left-[-9999px] top-0" aria-hidden="true">
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="career-name">Full Name <span className="text-red-500">*</span></label>
                    <input id="career-name" className={inputCls} required value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="career-name" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="career-phone">Phone <span className="text-red-500">*</span></label>
                    <input id="career-phone" className={inputCls} required type="tel" pattern="[0-9+ -]{10,15}" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" data-testid="career-phone" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="career-email">Email <span className="text-red-500">*</span></label>
                    <input id="career-email" className={inputCls} required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="career-email" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="career-expertise">Area of Expertise</label>
                    <select id="career-expertise" className={`${inputCls} appearance-none`} value={form.expertise} onChange={set("expertise")} data-testid="career-expertise">
                      <option value="" className="bg-[#0F0F14]">Select</option>
                      {ACTIVITIES.map((a) => (
                        <option key={a.slug} value={a.name} className="bg-[#0F0F14]">{a.name}</option>
                      ))}
                      <option value="Event Management" className="bg-[#0F0F14]">Event Management</option>
                      <option value="Anchoring" className="bg-[#0F0F14]">Anchoring</option>
                      <option value="Other" className="bg-[#0F0F14]">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="career-experience">Experience</label>
                    <input id="career-experience" className={inputCls} value={form.experience} onChange={set("experience")} placeholder="e.g. 3 years teaching Hip-Hop" data-testid="career-experience" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="career-role">Preferred Role</label>
                    <input id="career-role" className={inputCls} value={form.role} onChange={set("role")} placeholder="e.g. Dance Instructor" data-testid="career-role" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="career-message">Message</label>
                    <textarea id="career-message" rows="4" className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about yourself" data-testid="career-message" />
                  </div>
                  <FileField label="Resume / CV" name="resume" file={resume} onChange={(e) => setResume(e.target.files[0] || null)} testid="career-resume" />
                  <FileField label="Portfolio (optional)" name="portfolio" file={portfolio} onChange={(e) => setPortfolio(e.target.files[0] || null)} testid="career-portfolio" />
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-red-600 via-rose-600 to-red-700 px-8 py-4 font-display text-xs font-extrabold uppercase tracking-[0.18em] text-white shadow-[0_0_24px_rgba(220,38,38,0.35)] transition-all hover:shadow-[0_0_38px_rgba(220,38,38,0.55)] disabled:opacity-60" data-testid="career-submit">
                      {loading && <Loader2 size={16} className="animate-spin" />}
                      {loading ? "Submitting…" : "Submit Application"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Careers;
